import { BookOpen, FileText, Users, CheckCircle, Clock, Send } from 'lucide-react';
import { SafeLabsCTA } from '../components/SafeLabsCTA';

const ResearchPage = () => {
  const researchAreas = [
    {
      title: 'AI-Mediated Harm Patterns',
      description: 'Classification and analysis of how AI systems can facilitate, amplify, or fail to prevent harm to children in digital environments.',
    },
    {
      title: 'Governance Effectiveness',
      description: 'Evaluating the effectiveness of safeguarding governance frameworks, compliance mechanisms, and intervention protocols.',
    },
    {
      title: 'Behavioural Indicators',
      description: 'Research into early detection signals, escalation patterns, and behavioural markers that precede exploitation events.',
    },
    {
      title: 'Platform Accountability',
      description: 'Studies on platform design choices, algorithmic impacts on child safety, and the relationship between engagement metrics and harm.',
    },
    {
      title: 'Cross-Jurisdictional Standards',
      description: 'Comparative analysis of regulatory approaches to child safety across jurisdictions and their effectiveness.',
    },
    {
      title: 'Privacy-Preserving Safeguarding',
      description: 'Methods for maintaining child protection standards while preserving privacy rights and minimising surveillance.',
    },
  ];

  const submissionProcess = [
    { step: '1', title: 'Submission', description: 'Authors submit research papers through our secure portal for initial review.' },
    { step: '2', title: 'Editorial Review', description: 'The editorial board assesses scope, methodology, and relevance to SAFE\'s mission.' },
    { step: '3', title: 'Peer Review', description: 'Qualified reviewers from relevant disciplines provide structured feedback.' },
    { step: '4', title: 'Revision & Acceptance', description: 'Authors revise based on feedback. Accepted papers are published in the SAFE Research Journal.' },
  ];

  return (
    <div data-testid="research-page">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-16 md:py-28">
        <div className="max-w-7xl mx-auto container-padding">
          <p className="caption text-cyan-400 mb-3 md:mb-4">SAFE Labs</p>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-white mb-4 md:mb-6">
            Research & Publications
          </h1>
          <p className="text-base md:text-xl text-slate-300 leading-relaxed max-w-3xl">
            The SAFE Research Journal publishes peer-reviewed research advancing the science of AI-mediated child safeguarding. We serve as the reviewing entity that approves papers and research contributing to the global evidence base.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 bg-cyan-700 text-white">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <p className="text-xs uppercase tracking-widest text-cyan-200 mb-4">Our Mission</p>
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6 max-w-3xl mx-auto">
            Building the world's most rigorous evidence base for child safety governance in AI-mediated environments.
          </h2>
          <p className="text-lg text-cyan-100 max-w-2xl mx-auto">
            Every governance decision in SAFE is grounded in peer-reviewed research. We don't just build standards — we prove they work.
          </p>
        </div>
      </section>

      {/* Research Areas */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <p className="caption text-cyan-700 mb-4">Scope</p>
          <h2 className="heading-2 mb-4">Research Areas</h2>
          <p className="body-large mb-12 max-w-3xl">
            The SAFE Research Journal accepts submissions across the following domains. Interdisciplinary research bridging multiple areas is encouraged.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {researchAreas.map((area, index) => (
              <div key={index} className="card-institutional" data-testid={`research-area-${index}`}>
                <div className="w-10 h-10 bg-slate-900 text-white flex items-center justify-center mb-4">
                  <BookOpen className="w-5 h-5" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">{area.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{area.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Peer Review Process */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="caption text-cyan-700 mb-4">Process</p>
              <h2 className="heading-2 mb-6">Peer Review</h2>
              <p className="body-large mb-8">
                All submissions undergo rigorous peer review by qualified experts in relevant disciplines. SAFE acts as the reviewing entity — maintaining independence, quality, and academic integrity.
              </p>
              <div className="bg-slate-50 border border-slate-200 p-6">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-3">Review Standards</p>
                <ul className="space-y-3">
                  {[
                    'Methodological rigour and reproducibility',
                    'Ethical compliance and child safety considerations',
                    'Contribution to safeguarding evidence base',
                    'Practical applicability to governance frameworks',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="space-y-6">
              {submissionProcess.map((step, index) => (
                <div key={index} className="flex gap-4" data-testid={`review-step-${index}`}>
                  <div className="w-10 h-10 bg-slate-900 text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">
                    {step.step}
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">{step.title}</h4>
                    <p className="text-sm text-slate-600">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Published Papers (Placeholder) */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <p className="caption text-cyan-700 mb-4">Publications</p>
          <h2 className="heading-2 mb-4">Published Research</h2>
          <p className="body-large mb-12 max-w-3xl">
            Accepted papers will be published here as the journal launches. Initial publications are expected to cover foundational governance methodology and harm taxonomy validation.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                title: 'SAFE-001: Harm Taxonomy for AI-Mediated Child Safeguarding',
                status: 'In Preparation',
                authors: 'SAFE Governance Council',
                type: 'Foundational Standard',
              },
              {
                title: 'Escalation Patterns in AI-Mediated Environments: A 6-24 Hour Window Analysis',
                status: 'In Preparation',
                authors: 'SAFE Labs Research Team',
                type: 'Empirical Research',
              },
              {
                title: 'Deterministic Governance Rules for Child Protection: Architecture and Validation',
                status: 'Planned',
                authors: 'SAFE Technical Team',
                type: 'Technical Paper',
              },
              {
                title: 'Cross-Jurisdictional Regulatory Mapping for Online Child Safety',
                status: 'Planned',
                authors: 'Legal & Regulatory Council Seat',
                type: 'Comparative Analysis',
              },
            ].map((paper, index) => (
              <div key={index} className="bg-white border border-slate-200 p-6" data-testid={`paper-${index}`}>
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span className="text-xs uppercase tracking-widest text-slate-500">{paper.type}</span>
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2 leading-snug">{paper.title}</h3>
                <p className="text-sm text-slate-500 mb-4">{paper.authors}</p>
                <div className="flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-amber-600" />
                  <span className="text-xs font-medium text-amber-600">{paper.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial Board */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl mx-auto text-center">
            <p className="caption text-cyan-400 mb-4">Editorial Board</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
              Review Committee
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              The SAFE Research Journal editorial board is being assembled from the Governance Council's Academic Research seat and affiliated institutional researchers. Board members will be announced upon journal launch.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { label: 'Editorial Board Members', value: 'Forming' },
                { label: 'Review Turnaround', value: '4-6 Weeks' },
                { label: 'Publication Frequency', value: 'Quarterly' },
              ].map((stat, i) => (
                <div key={i} className="bg-slate-800 border border-slate-700 p-6 text-center">
                  <p className="text-2xl font-bold text-white mb-1">{stat.value}</p>
                  <p className="text-xs uppercase tracking-widest text-slate-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <h2 className="heading-2 mb-6">Submit Research</h2>
          <p className="body-large max-w-2xl mx-auto mb-6">
            We welcome submissions from researchers, academics, and practitioners working in child safety, AI governance, digital rights, and related fields.
          </p>
          <p className="text-sm text-slate-500 max-w-xl mx-auto mb-10">
            The submission portal will open upon journal launch. To register interest or discuss a potential submission, please contact the editorial team.
          </p>
          <a
            href="mailto:governance@safestandard.ai"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="btn-submit-research"
          >
            <Send className="w-4 h-4" />
            Contact Editorial Team
          </a>
        </div>
      </section>
      <SafeLabsCTA />
    </div>
  );
};

export default ResearchPage;
